// Copy and Narrate for the structured working: readable markdown for the clipboard,
// spoken prose for text-to-speech — both from the model, never from the DOM.

import { feedBlocks, formatDuration, verdictOf, chainOf } from "./journalModel";

function tableMarkdown(table) {
  if (!table || !Array.isArray(table.columns)) return "";
  const header = `| ${table.columns.map((c) => c.label || "").join(" | ")} |`;
  const rule = `| ${table.columns.map((c) => (c.align === "num" ? "---:" : "---")).join(" | ")} |`;
  const rows = (table.rows || []).map((r) => `| ${(r.cells || []).map((c) => c.t).join(" | ")} |`);
  return [table.caption ? `_${table.caption}_` : "", header, rule, ...rows].filter(Boolean).join("\n");
}

// THE CLIPBOARD HAS NO ROOM TO FOLD, so a refusal is copied whole: the row's lead plus
// the remainder that sits behind its disclosure. A lead that had to be cut mid-sentence
// ends in an ellipsis on screen; rejoined to its tail, that mark is noise.
function fullReason(block) {
  const lead = String(block.reason || "").replace(/…$/, "");
  return [lead, String(block.reason_more || "")].filter(Boolean).join(" ");
}

function blockMarkdown(block) {
  switch (block.kind) {
    case "reasoning":
      return `> ${block.text}${block.more && block.more.length ? `\n> ${block.more.join("\n> ")}` : ""}`;
    case "action":
      return `- **${block.label}** ${block.detail}${block.ok === false ? ` _(refused${block.reason ? `: ${fullReason(block)}` : ""})_` : ""}${block.duration_ms ? ` · ${formatDuration(block.duration_ms)}` : ""}\n  - because: ${block.purpose}`;
    case "evidence_table":
      return tableMarkdown(block);
    case "evidence_chart": {
      // The metric leads, for the same reason it does on screen: a bare list of labels and
      // values does not say what was measured, and the caption never carries the metric.
      const heading = [block.metric_label, block.caption].filter(Boolean).join(" — ");
      return `_${heading}_\n${(block.bars || []).map((b) => `- ${b.label}: ${b.text}`).join("\n")}\n\n${tableMarkdown(block.table)}`;
    }
    case "calc_result": {
      const pairs = (block.pairs || []).map((p) => `- ${p.key}: ${p.value}`).join("\n");
      return `**Calculated** — ${block.purpose}\n${pairs || block.text || ""}`;
    }
    case "finding":
      return `**Finding:** ${block.text}`;
    case "deviation":
      return `_${block.text}_`;
    case "chain":
      return `**The answer as a picture${block.scope ? ` — ${block.scope}` : ""}:** ${(block.nodes || []).map((n) => `${n.label} ${n.big} (${n.small})`).join(" → ")}${block.counterpart ? ` ← ${block.counterpart.label} ${block.counterpart.big} (${block.counterpart.small})` : ""}`;
    default:
      return "";
  }
}

export function toMarkdown(model) {
  if (!model || !model.run) return "";
  const run = model.run;
  const lines = [`# ${run.question || "The working"}`, ""];
  const facts = [run.method, verdictOf(model).label, formatDuration(run.duration_ms), run.data_through]
    .filter(Boolean);
  lines.push(`**${facts.join(" · ")}**`, "");
  const chain = chainOf(model);
  if (chain) lines.push(blockMarkdown(chain), "");
  // THE RUN IN ORDER, which is what the panel shows: no step headings to reconcile it
  // against. A block that a step claimed says so on its own line.
  lines.push("## What the analysis did, in order", "");
  feedBlocks(model).forEach((block) => {
    const md = blockMarkdown(block);
    if (!md) return;
    lines.push(md, "");
  });
  if (model.footer && Array.isArray(model.footer.facts)) {
    lines.push("---", model.footer.facts.join(" · "));
  }
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

export function toProse(model) {
  if (!model || !model.run) return "";
  const run = model.run;
  const parts = [];
  parts.push(`The working for: ${run.question || "this question"}.`);
  parts.push(`Method: ${run.method || "the analysis"}. ${verdictOf(model).label}.`);
  feedBlocks(model).forEach((block) => {
    if (block.kind === "action") parts.push(`${block.label} ${block.detail}, because ${block.purpose}.${block.ok === false && block.reason ? ` Refused: ${block.reason}` : ""}`);
    else if (block.kind === "evidence_table") parts.push(`A table of ${block.total_rows} rows: ${block.caption}.`);
    else if (block.kind === "evidence_chart") parts.push(`A chart of ${block.metric_label} movement: ${block.caption}.`);
    else if (block.kind === "calc_result") parts.push(`Calculated ${block.purpose}: ${(block.pairs || []).map((p) => `${p.key} ${p.value}`).join(", ") || block.text || ""}.`);
    else if (block.kind === "finding") parts.push(`Finding: ${block.text}`);
    else if (block.kind === "deviation") parts.push(block.text);
    else if (block.kind === "reasoning") parts.push(block.text);
  });
  if (model.footer && Array.isArray(model.footer.facts)) parts.push(model.footer.facts.join(". ") + ".");
  return parts.join(" ").replace(/\s+/g, " ").replace(/\.\./g, ".").trim();
}
